import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import "./styles/tokens.css";

import * as Sentry from "@sentry/react";
import { isNetworkError, redactSignupId } from "./utils/sentryUtils";
import { reloadOnStaleChunk } from "./utils/staleChunkUtils";

window.addEventListener("vite:preloadError", reloadOnStaleChunk);

try {
  if (window.appConfig.sentry_dsn?.length > 0) {
    const release = `build${import.meta.env.VITE_BUILD_NUMBER || "unknown"}`;
    console.log("Initialising Sentry with release:", release);
    Sentry.init({
      dsn: window.appConfig.sentry_dsn,
      integrations: [Sentry.browserTracingIntegration()],
      release: release,
      environment: window.appConfig.sentry_environment,
      // v10 gates user IP/geo inference behind this flag; preserves pre-v10 behaviour.
      sendDefaultPii: true,
      // Tag fetch-level network failures so they're filterable in issue search
      // and alert rules. Preserves Sentry's default per-call-site grouping —
      // the tag gives us alertability without collapsing useful context.
      beforeSend(event, hint) {
        if (isNetworkError(hint?.originalException)) {
          event.tags = { ...event.tags, network_error: true };
        }
        if (event.request?.url) {
          event.request.url = redactSignupId(event.request.url);
        }
        return event;
      },
      // The default breadcrumbs integration records the URL of every fetch and
      // every navigation, so the redaction in beforeSend is not enough on its own.
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.data?.url) {
          breadcrumb.data.url = redactSignupId(breadcrumb.data.url);
        }
        if (breadcrumb.category === "navigation" && breadcrumb.data) {
          breadcrumb.data.from = redactSignupId(breadcrumb.data.from);
          breadcrumb.data.to = redactSignupId(breadcrumb.data.to);
        }
        return breadcrumb;
      },
      // Note: ignoreErrors is needed in addition to allowUrls/denyUrls because
      // injected browser scripts often throw errors with no stacktrace,
      // bypassing URL-based filtering entirely.
      ignoreErrors: [
        // Errors from privacy browsers blocking Google Analytics/tracking scripts
        "invalid origin",
        // Chrome iOS browser/extension issues (password managers, autofill)
        // causing infinite recursion - no useful stacktrace, not our code
        "Maximum call stack size exceeded",
        // Common browser extension and third-party errors
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        // DuckDuckGo Mobile browser internal WKWebView messaging errors (iOS) - not our code
        "WKWebView API client did not respond to this postMessage",
        // DuckDuckGo Mobile browser internal feature flag errors - not our code
        /feature named .* was not found/,
        // Safari dispatches CustomEvent as unhandled promise rejection (no stacktrace,
        // isTrusted: false) — browser extension or Safari internal noise, not our code
        /Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection/,
        // Onfido SDK internal module load failures — third-party SDK rejecting a plain
        // object when a sub-module fails to fetch (no stacktrace, bypasses allowUrls)
        /Object captured as promise rejection with keys: analyticsError/,
      ],
      allowUrls: [
        // Only capture errors from our own code
        /client\.juno\.legal/,
      ],
      denyUrls: [
        // Browser extensions (defence in depth)
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
      ],
    });
  }
} catch (e) {
  console.error("Failed to initialise Sentry", e);
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
